"use client";

/**
 * SampleBrief — the sanitized MSFT sample brief card.
 *
 * Content rules (regulatory — do not loosen):
 *  - The synthesis paragraph is the APPROVED text, VERBATIM. Do not edit it.
 *  - Signal rows are factual observations + the analyzer's own confidence.
 *  - NO action / buy / sell / hold language anywhere on the card.
 *  - The disclaimer line is part of the component, not optional page copy.
 *
 * Motion: the card reveals on scroll (`useReveal`), then the conviction
 * ring + numeral count up to 7.0 (`useCountUp`, gated on first viewport
 * entry). Under prefers-reduced-motion both hooks render the completed
 * state immediately — full ring, 7.0 shown.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useCountUp, useReveal } from "@/lib/motion";
import { cn } from "@/lib/utils/cn";
import { MONO, SERIF, LINE } from "./shared";

export interface SampleBriefProps {
  className?: string;
}

const RING_R = 18;
const RING_C = 2 * Math.PI * RING_R; // ≈ 113.1
const CONVICTION = 7.0;

interface SignalRow {
  name: string;
  note: string;
  glyph: string;
  color: string;
  conf: string;
}

/** Factual signal rows from the sanitized MSFT cycle (no advice language). */
const SIGNALS: SignalRow[] = [
  {
    name: "FUNDAMENTAL",
    note: "Elite returns on capital; moat intact",
    glyph: "+",
    color: "#10B981",
    conf: "8.4",
  },
  {
    name: "VALUATION",
    note: "88th percentile of the DCF distribution",
    glyph: "−",
    color: "#EF4444",
    conf: "7.9",
  },
  {
    name: "SMART MONEY",
    note: "Six-executive cluster sale on the tape",
    glyph: "−",
    color: "#EF4444",
    conf: "8.1",
  },
  {
    name: "CATALYST",
    note: "Q4 earnings July 22",
    glyph: "!",
    color: "#F59E0B",
    conf: "9.0",
  },
];

const WATCH_ITEMS = [
  "Q4 earnings July 22: does capex start converting to revenue?",
  "Executive cluster sale: one-off diversification or a pattern?",
];

const SOURCES = ["SEC EDGAR", "FMP FUNDAMENTALS", "POLYGON PRICES", "DCF MONTE CARLO"];

export function SampleBrief({ className }: SampleBriefProps) {
  const revealRef = useReveal<HTMLDivElement>();

  // First-viewport-entry gate for the conviction count-up.
  const [inView, setInView] = useState(false);
  const ioRef = useRef<IntersectionObserver | null>(null);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      revealRef(node);
      ioRef.current?.disconnect();
      ioRef.current = null;
      if (!node) return;
      if (typeof IntersectionObserver === "undefined") {
        setInView(true);
        return;
      }
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        },
        { threshold: 0.3 },
      );
      io.observe(node);
      ioRef.current = io;
    },
    [revealRef],
  );
  useEffect(() => () => ioRef.current?.disconnect(), []);

  const conviction = useCountUp(CONVICTION, { start: inView, durationMs: 900 });

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-[14px] border bg-[#16203A] px-6 py-5 shadow-[0_24px_60px_-24px_rgba(0,0,0,.55)]",
        className,
      )}
      style={{ borderColor: LINE }}
      aria-label="Sample Compass brief for Microsoft"
    >
      {/* Header: ticker + conviction ring */}
      <div className="mb-3 flex items-center gap-3.5">
        <div className="text-base text-[#F1F5F9]" style={{ fontFamily: MONO }}>
          MSFT <span className="text-[0.7rem] text-[#64748B]">NASDAQ</span>
        </div>
        <span
          className="rounded-full border border-gold/50 px-2.5 py-[3px] text-[0.6rem] tracking-[0.12em] text-gold"
          style={{ fontFamily: MONO }}
        >
          SAMPLE BRIEF
        </span>
        <div className="ml-auto flex items-center gap-[9px]">
          <svg viewBox="0 0 42 42" aria-hidden className="h-[42px] w-[42px] -rotate-90">
            <circle cx={21} cy={21} r={RING_R} fill="none" strokeWidth={4} stroke="rgba(148,163,184,.15)" />
            <circle
              cx={21}
              cy={21}
              r={RING_R}
              fill="none"
              strokeWidth={4}
              strokeLinecap="round"
              stroke="#F59E0B"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - conviction / 10)}
            />
          </svg>
          <div>
            <div className="font-tabular text-[1.15rem] leading-none text-[#F1F5F9]" style={{ fontFamily: SERIF }}>
              {conviction.toFixed(1)}
            </div>
            <div className="mt-1 text-[0.55rem] tracking-[0.14em] text-[#64748B]" style={{ fontFamily: MONO }}>
              CONVICTION
            </div>
          </div>
        </div>
      </div>

      {/* Approved synthesis text — VERBATIM, do not edit. */}
      <p className="text-[1.05rem] leading-[1.55] text-[#F1F5F9]" style={{ fontFamily: SERIF }}>
        An exceptional business at a demanding price. Shares sit at the 88th percentile of
        our DCF distribution; the median is <span className="font-tabular">$353</span>. Elite
        fundamentals, no margin of safety at current levels.
      </p>

      {/* Signal rows */}
      <div className="mt-4 divide-y rounded-[10px] border bg-[#0A101F]" style={{ borderColor: LINE }}>
        {SIGNALS.map((s) => (
          <div
            key={s.name}
            className="flex items-baseline gap-3 px-3.5 py-2 text-[0.7rem]"
            style={{ fontFamily: MONO, borderColor: LINE }}
          >
            <span className="w-[92px] shrink-0 tracking-[0.08em] text-[#64748B]">{s.name}</span>
            <span className="min-w-0 flex-1 truncate text-[#94A3B8]">{s.note}</span>
            <span className="font-medium" style={{ color: s.color }}>
              {s.glyph}
            </span>
            <span className="font-tabular text-[#64748B]">{s.conf}</span>
          </div>
        ))}
      </div>

      {/* Watch items */}
      <div className="mt-4">
        <div className="text-[0.6rem] tracking-[0.14em] text-[#64748B]" style={{ fontFamily: MONO }}>
          WATCH ITEMS
        </div>
        <ul className="mt-1.5 space-y-1">
          {WATCH_ITEMS.map((w) => (
            <li key={w} className="flex gap-2 text-[0.82rem] leading-[1.5] text-[#94A3B8]">
              <span aria-hidden className="mt-[2px] text-gold">
                ▸
              </span>
              {w}
            </li>
          ))}
        </ul>
      </div>

      {/* Source chips */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {SOURCES.map((src) => (
          <span
            key={src}
            className="rounded-full border px-2.5 py-1 text-[0.58rem] tracking-[0.1em] text-[#94A3B8]"
            style={{ fontFamily: MONO, borderColor: "rgba(148,163,184,0.28)" }}
          >
            {src}
          </span>
        ))}
      </div>

      {/* Built-in disclaimer */}
      <p className="mt-4 border-t pt-3 text-[0.68rem] leading-[1.5] text-[#64748B]" style={{ borderColor: LINE }}>
        Sample brief. Scores and evidence traced to source. Educational research, not
        investment advice.
      </p>
    </div>
  );
}
